import { useEffect, useState } from "react";
import { CalendarDays, Loader2, RefreshCw, Check, X, Inbox } from "lucide-react";
import { toast } from "sonner";
import OwnerLayout from "@/components/owner/OwnerLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { SURVEY_STATUS_LABELS, SURVEY_STATUS_STYLES, formatDate } from "@/components/booking/bookingMeta";

const FILTERS = [
  { v: "all", l: "Semua" },
  { v: "pending", l: "Menunggu Konfirmasi" },
  { v: "confirmed", l: "Dikonfirmasi" },
  { v: "cancelled", l: "Dibatalkan" },
  { v: "completed", l: "Selesai" },
];

export default function OwnerSurveysPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [acting, setActing] = useState(null);

  const load = (f = filter) => {
    setLoading(true);
    apiClient.get("/owner/surveys", { params: { status: f } })
      .then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const act = async (id, action) => {
    setActing(id + action);
    try {
      await apiClient.post(`/owner/surveys/${id}/${action}`);
      toast.success(action === "confirm" ? "Jadwal survei dikonfirmasi." : "Jadwal survei dibatalkan.");
      load(filter);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  return (
    <OwnerLayout>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><CalendarDays size={22} className="text-primary" /> Report Survei</h1>
          <p className="text-sm text-muted-foreground">Jadwal survei/kunjungan dari calon penyewa.</p>
        </div>
        <button onClick={() => load(filter)} data-testid="owner-surveys-refresh"
          className="grid place-items-center w-10 h-10 rounded-full border border-border hover:bg-secondary/60 transition-colors">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="owner-surveys-filters">
        {FILTERS.map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`owner-surveys-filter-${f.v}`}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${filter === f.v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary/60"}`}>
            {f.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" size={26} /></div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground" data-testid="owner-surveys-empty">
          <Inbox size={30} className="mx-auto mb-2" /> Belum ada jadwal survei.
        </div>
      ) : (
        <div className="space-y-3" data-testid="owner-surveys-list">
          {rows.map((s, i) => (
            <div key={s.id} className="bg-card border border-border rounded-2xl p-4" data-testid={`owner-survey-${i}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-semibold">{s.property_name || "Properti"}{s.room_number ? ` · Kamar ${s.room_number}` : ""}</p>
                  <p className="text-sm text-muted-foreground">Customer: {s.customer_name || s.customer_email || "-"}</p>
                  <p className="mt-1 text-sm"><span className="font-medium">{formatDate(s.survey_date)}</span> · {s.survey_time || "-"}</p>
                  {s.notes && <p className="mt-1 text-sm text-muted-foreground">“{s.notes}”</p>}
                </div>
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${SURVEY_STATUS_STYLES[s.status] || ""}`} data-testid={`owner-survey-status-${i}`}>
                  {SURVEY_STATUS_LABELS[s.status] || s.status}
                </span>
              </div>
              {(s.status === "pending" || s.status === "confirmed") && (
                <div className="mt-3 flex gap-2">
                  {s.status === "pending" && (
                    <button onClick={() => act(s.id, "confirm")} disabled={acting === s.id + "confirm"} data-testid={`owner-survey-confirm-${i}`}
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold text-emerald-700 border border-emerald-200 hover:bg-emerald-50 transition-colors disabled:opacity-60">
                      {acting === s.id + "confirm" ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Konfirmasi
                    </button>
                  )}
                  <button onClick={() => act(s.id, "cancel")} disabled={acting === s.id + "cancel"} data-testid={`owner-survey-cancel-${i}`}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold text-rose-600 border border-rose-200 hover:bg-rose-50 transition-colors disabled:opacity-60">
                    {acting === s.id + "cancel" ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />} Batalkan
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </OwnerLayout>
  );
}
